import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { memberFlagsApi } from '../../api/flagSystemApi';
import { flagError, flagFieldErrors, validateFlagMemberMembershipNumberChange } from '../../utils/flagSystem';
import { Field } from './FlagSystemCommon';

const renameError = (failure) => {
  const status = failure && failure.response && failure.response.status;
  if (!failure || !failure.response) return 'The flag-user number update may have completed, but the result is uncertain. Refresh the current flag user before retrying.';
  if (status === 404) return 'The flag user was not found or may have changed. Refresh the current flag user before trying again.';
  return flagError(failure, 'Unable to update the flag-user membership number.');
};

const FlagMemberMembershipNumberDialog = ({ account, scope, memberId, onAccessError, onRefresh, onRenamed }) => {
  const [visible, setVisible] = useState(false);
  const [newMemberId, setNewMemberId] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const requestVersion = useRef(0);
  const close = () => {
    if (!saving) {
      requestVersion.current += 1;
      setVisible(false); setNewMemberId(''); setComment(''); setErrors({}); setError('');
    }
  };
  const open = () => {
    requestVersion.current += 1;
    setVisible(true); setNewMemberId(''); setComment(''); setErrors({}); setError('');
  };
  const save = async () => {
    const validation = validateFlagMemberMembershipNumberChange(memberId, newMemberId, comment, account.isCustomer);
    setErrors(validation);
    if (Object.keys(validation).length || saving) return;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setSaving(true); setError('');
    try {
      const saved = await memberFlagsApi.changeMembershipNumber(scope, memberId, newMemberId, comment);
      if (requestVersion.current !== version) return;
      if (!saved.changed) {
        setError('The server reported that the flag-user membership number was not changed. Refresh the current flag user before trying again.');
        return;
      }
      onRenamed(saved.memberId, saved);
    } catch (failure) {
      if (requestVersion.current !== version) return;
      onAccessError(failure); setError(renameError(failure)); setErrors(previous => ({ ...previous, ...flagFieldErrors(failure) }));
    } finally {
      if (requestVersion.current === version) setSaving(false);
    }
  };
  const validation = validateFlagMemberMembershipNumberChange(memberId, newMemberId, comment, account.isCustomer);
  const canSave = newMemberId.trim() !== memberId.trim() && !Object.keys(validation).length;
  return <>
    <Button label="Edit flag-user number" icon="pi pi-pencil" className="p-button-text" onClick={open} />
    <Dialog header="Edit flag-user membership number" visible={visible} className="flag-editor flag-member-rename-dialog"
      modal closable={!saving} onHide={close}
      footer={<><Button label="Cancel" className="p-button-text" disabled={saving} onClick={close} />
        <Button label="Save flag-user number" icon="pi pi-check" className="p-button-success" loading={saving}
          disabled={saving || !canSave} onClick={save} /></>}>
      <Message severity="info" text="This changes only the flag-user membership number across all locations for this customer. Existing flags remain attached. It does not update the membership account or LMS." />
      <Field id="current-flag-member-id" label="Current flag-user membership number">
        <InputText id="current-flag-member-id" value={memberId} readOnly />
      </Field>
      <Field id="new-flag-member-id" label="New flag-user membership number" error={errors.newMemberId}>
        <InputText id="new-flag-member-id" value={newMemberId} maxLength={200} disabled={saving}
          className={errors.newMemberId ? 'p-invalid' : ''} onChange={event => { setNewMemberId(event.target.value); setError(''); }} />
      </Field>
      <Field id="flag-member-rename-comment" label={`Comment / reason${account.isCustomer ? ' (optional)' : ' (required)'}`} error={errors.comment}>
        <InputTextarea id="flag-member-rename-comment" value={comment} maxLength={1000} rows={3} disabled={saving}
          className={errors.comment ? 'p-invalid' : ''} onChange={event => { setComment(event.target.value); setError(''); }} />
      </Field>
      {error && <><Message severity="error" text={error} />
        {error.includes('not found or may have changed') && <Button label="Refresh current flag user" className="p-button-text" disabled={saving} onClick={onRefresh} />}</>}
    </Dialog>
  </>;
};

export default FlagMemberMembershipNumberDialog;
